import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/shared/components/ui/tooltip";

/**
 * Pipeline tab bar (design-system.md §4). The tab order IS the pipeline order —
 * never reorder. Overview is the index route; the rest are child paths. Each tab
 * carries a hover hint of what that stage does.
 */
const TABS: { to: string; label: string; end?: boolean; hint: string }[] = [
  { to: "", label: "Overview", end: true, hint: "Campaign summary: KPIs, pipeline stage, next action and latest activity." },
  { to: "discovery", label: "Discovery", hint: "Run discovery for this campaign (AI or Places); screened for dedup + suppression." },
  { to: "leads", label: "Leads", hint: "Review discovered leads — approve or reject candidates; screening stays visible." },
  { to: "enrichment", label: "Enrichment", hint: "Find contact emails for approved leads (Hunter waterfall + deliverability check)." },
  { to: "audience", label: "Audience", hint: "Freeze approved leads into a snapshot; suppression re-checks at the gate." },
  { to: "sequences", label: "Sequences", hint: "Build the outreach sequence: steps, delays and templates." },
  { to: "analytics", label: "Analytics", hint: "Funnel + per-step open/click/reply rates for this campaign." },
];

export function PipelineTabs({ campaignId }: { campaignId: string }) {
  const { pathname } = useLocation();
  const base = `/campaigns/${campaignId}`;
  // Active computed here, not via NavLink's function className — Radix Slot
  // (the tooltip trigger) stringifies a function className and breaks styling.

  return (
    <nav
      aria-label="Campaign pipeline"
      className="mt-[18px] flex gap-6 overflow-x-auto overflow-y-hidden border-b border-ge-border-subtle"
    >
      {TABS.map((tab) => {
        const to = tab.to ? `${base}/${tab.to}` : base;
        const active = tab.end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
        return (
          <Tooltip key={tab.label}>
            <TooltipTrigger asChild>
              <NavLink
                to={to}
                end={tab.end}
                data-testid={`tab-${tab.label.toLowerCase()}`}
                className={cn(
                  "-mb-px whitespace-nowrap border-b-2 px-[2px] pb-[10px] text-[13px] transition-colors",
                  active
                    ? "border-ge-accent font-semibold text-ge-primary"
                    : "border-transparent font-medium text-ge-secondary hover:text-ge-primary",
                )}
              >
                {tab.label}
              </NavLink>
            </TooltipTrigger>
            <TooltipContent side="bottom">{tab.hint}</TooltipContent>
          </Tooltip>
        );
      })}
    </nav>
  );
}
